import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Select,
  Spinner,
  Alert,
  Chip,
  Tabs,
  EmptyState,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { DANGER_BTN, NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  AdminApiError,
  type UserListItem,
  type UserDetail,
  type CognitoState,
  type MembershipState,
  type UserStatusFilter,
  type PortalRole,
} from '../lib/adminApiClient';

/**
 * User management (user:manage).
 * - Search/list end-users; filter by role + lifecycle status.
 * - Invite a new user (Cognito invite-email + DB row).
 * - Per-user: set portal role (synced to Cognito groups), suspend/reactivate
 *   (Cognito disable/enable), soft-delete (30-day grace) + restore.
 * - Link to the audit log filtered to that user.
 * Server is the source of truth — every action re-checks the capability and is
 * audited. NOTE: granular admin-STAFF roles (superadmin/editor/viewer) are a
 * separate deferred effort; `role` here is the consumer-facing portal role.
 */

const ROLES: PortalRole[] = ['consumer', 'driver', 'vendor', 'admin'];

const fmtDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString() : '—');

const fullName = (u: UserListItem): string =>
  u.displayName || [u.firstname, u.lastname].filter(Boolean).join(' ') || '—';

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' =>
  s === 'active' ? 'success' : s === 'unverified' ? 'warning' : s === 'deleted' ? 'error' : 'default';

export default function UserManagement() {
  const caps = useCapabilities();
  const canView = caps.has('user:read');
  const canManage = caps.has('user:manage');
  const canMembership = caps.has('subscription:manage');

  const [status, setStatus] = useState<UserStatusFilter>('active');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<UserListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listUsers({ status, q: search || undefined, limit: 50 });
      setItems(res.items);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await adminApiClient.listUsers({
        status,
        q: search || undefined,
        before: nextCursor,
        limit: 50,
      });
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof Error ? err.message : 'Failed to load more' });
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, status, search]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const onChanged = (kind: 'success' | 'error', msg: string, updated?: UserListItem) => {
    setFlash({ kind, msg });
    if (updated) setItems((prev) => prev.map((u) => (u.glpUserId === updated.glpUserId ? updated : u)));
  };

  if (caps.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (!canView) {
    return <Alert severity="error">You don’t have permission to view users.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
          User management
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            color="primary"
            ariaLabel="Invite a new user"
            buttonText={inviteOpen ? 'Close invite' : 'Invite user'}
            onClick={() => setInviteOpen((v) => !v)}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Search users, manage roles, suspend or delete accounts. Every change is audited.
      </Typography>

      {!canManage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      {canManage && inviteOpen && <InviteForm onDone={(k, m) => { onChanged(k, m); if (k === 'success') void load(); }} />}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Tabs
          ariaLabel="User status"
          value={status}
          onChange={(_e, v) => setStatus(v as UserStatusFilter)}
          tabs={[
            { value: 'active', label: 'Active' },
            { value: 'unverified', label: 'Unverified' },
            { value: 'deleted', label: 'Deleted' },
            { value: 'all', label: 'All' },
          ]}
        />
        <Box sx={{ flex: 1 }} />
        <TextField
          label="Search name / email / ID"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          size="small"
          sx={{ width: 260 }}
        />
        <Button
          variant="outlined"
          sx={NEUTRAL_BTN}
          ariaLabel="Apply search"
          buttonText="Search"
          onClick={() => setSearch(searchDraft.trim())}
        />
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind === 'success' ? 'success' : 'error'}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState icon="person" title="No users" body="No users match this filter." />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((u) => (
              <UserCard key={u.glpUserId} user={u} canWrite={canManage} canMembership={canMembership} onChanged={onChanged} />
            ))}
          </Box>
          {nextCursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                sx={NEUTRAL_BTN}
                ariaLabel="Load more users"
                buttonText={loadingMore ? 'Loading…' : 'Load more'}
                disabled={loadingMore}
                onClick={() => void loadMore()}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function InviteForm({ onDone }: { onDone: (kind: 'success' | 'error', msg: string) => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PortalRole>('consumer');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    try {
      await adminApiClient.createUser({ email: e, role });
      onDone('success', `Invited ${e} (${role}). A Cognito invite email was sent.`);
      setEmail('');
      setRole('consumer');
    } catch (err) {
      onDone('error', err instanceof AdminApiError ? err.message : 'Failed to invite user');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <TextField
        label="Email"
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
        size="small"
        sx={{ width: 280 }}
      />
      <Select
        ariaLabel="Role for the invited user"
        label="Role"
        value={role}
        onChange={(_e, v) => setRole(v as PortalRole)}
        size="small"
        sx={{ width: 160 }}
        options={ROLES.map((r) => ({ value: r, label: r }))}
      />
      <Button
        variant="contained"
        color="primary"
        ariaLabel="Send invite"
        buttonText={busy ? 'Inviting…' : 'Send invite'}
        disabled={busy || !email.trim()}
        onClick={() => void submit()}
      />
    </Paper>
  );
}

function UserCard({
  user,
  canWrite,
  canMembership,
  onChanged,
}: {
  user: UserListItem;
  canWrite: boolean;
  canMembership: boolean;
  onChanged: (kind: 'success' | 'error', msg: string, updated?: UserListItem) => void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<{ user: UserDetail; cognito: CognitoState | null; membership: MembershipState } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [roleDraft, setRoleDraft] = useState<PortalRole>(user.role);

  const [membershipTier, setMembershipTier] = useState(user.subscriptionTier);
  const [expiry, setExpiry] = useState('');
  const [reason, setReason] = useState('');

  const isDeleted = user.status === 'deleted';

  const loadDetail = useCallback(async () => {
    setDetailLoading(true);
    try {
      const next = await adminApiClient.getUser(user.glpUserId);
      setDetail(next);
      setMembershipTier(next.membership.tier);
      setExpiry(next.membership.expiresAt ? new Date(new Date(next.membership.expiresAt).getTime() - new Date(next.membership.expiresAt).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to load user');
    } finally {
      setDetailLoading(false);
    }
  }, [user.glpUserId, onChanged]);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) void loadDetail();
  };

  const saveMembership = async () => {
    setBusy(true);
    try {
      await adminApiClient.setUserMembership(user.glpUserId, { tier: membershipTier, expiresAt: membershipTier === 'free' || !expiry ? null : new Date(expiry).toISOString(), reason: reason.trim() });
      onChanged('success', `Set ${user.email} membership to ${membershipTier}.`, { ...user, subscriptionTier: membershipTier });
      setReason('');
      await loadDetail();
    } catch (err) { onChanged('error', err instanceof Error ? err.message : 'Failed to update membership'); }
    finally { setBusy(false); }
  };

  const saveRole = async () => {
    if (roleDraft === user.role) return;
    setBusy(true);
    try {
      await adminApiClient.setUserRole(user.glpUserId, roleDraft);
      onChanged('success', `Set ${user.email} to ${roleDraft}.`, { ...user, role: roleDraft });
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to set role');
    } finally {
      setBusy(false);
    }
  };

  const setSuspended = async (disable: boolean) => {
    setBusy(true);
    try {
      if (disable) await adminApiClient.disableUser(user.glpUserId);
      else await adminApiClient.enableUser(user.glpUserId);
      onChanged('success', `${disable ? 'Suspended' : 'Reactivated'} ${user.email}.`);
      if (expanded) await loadDetail();
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to update access');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await adminApiClient.deleteUser(user.glpUserId);
      onChanged('success', `Soft-deleted ${user.email} (30-day grace).`, { ...user, status: 'deleted' });
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to delete user');
    } finally {
      setBusy(false);
    }
  };

  const doRestore = async () => {
    setBusy(true);
    try {
      await adminApiClient.restoreUser(user.glpUserId);
      onChanged('success', `Restored ${user.email}.`, { ...user, status: 'active' });
    } catch (err) {
      onChanged('error', err instanceof AdminApiError ? err.message : 'Failed to restore user');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {fullName(user)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {user.email} · {user.glpUserId}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={user.role} color={user.role === 'admin' ? 'primary' : 'default'} size="small" />
            <Chip label={user.status} color={statusColor(user.status)} size="small" />
            <Chip label={user.subscriptionTier} size="small" />
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
              joined {fmtDate(user.createdAt)}
            </Typography>
          </Box>
        </Box>
        {(canWrite || canMembership) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
            <Button
              variant="outlined"
          sx={NEUTRAL_BTN}
              ariaLabel="Manage user"
              buttonText={expanded ? 'Hide' : 'Manage'}
              disabled={busy}
              onClick={toggleExpand}
            />
          </Box>
        )}
      </Box>

      {(canWrite || canMembership) && expanded && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Spinner color="brand" />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {detail?.cognito && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Cognito: {detail.cognito.enabled ? 'enabled' : 'disabled'}
                  {detail.cognito.status ? ` · ${detail.cognito.status}` : ''}
                  {detail.user.emailVerified ? ' · email verified' : ' · email unverified'}
                </Typography>
              )}

              {canMembership && detail && (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle1">Membership</Typography>
                  <Typography variant="body2">Complimentary membership. No charge is made. Leave expiration blank for no expiration; an expired membership returns to Free.</Typography>
                  {detail.membership.hasExternalBilling && <Alert severity="warning">This account has a paid billing subscription. Manage that subscription before assigning complimentary membership.</Alert>}
                  <Select label="Membership tier" ariaLabel="Membership tier" value={membershipTier} onChange={(_e, v) => setMembershipTier(String(v))} options={['free', 'silver', 'gold', 'platinum'].map((value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }))} />
                  <TextField label="Expiration (your local time; blank means no expiration)" ariaLabel="Membership expiration" type="datetime-local" value={expiry} disabled={membershipTier === 'free'} onChange={(e) => setExpiry(e.target.value)} />
                  <TextField label="Reason for membership change" ariaLabel="Membership reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <Button buttonText="Save membership" ariaLabel="Save membership" variant="contained" onClick={() => void saveMembership()} disabled={busy || user.status !== 'active' || detail.membership.hasExternalBilling || reason.trim().length < 3 || reason.trim().length > 500 || (membershipTier !== 'free' && !!expiry && new Date(expiry).getTime() <= Date.now())} />
                </Paper>
              )}
              {canWrite && <>
              {/* Role */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Select
                  ariaLabel="Portal role"
                  label="Portal role"
                  value={roleDraft}
                  onChange={(_e, v) => setRoleDraft(v as PortalRole)}
                  size="small"
                  sx={{ width: 160 }}
                  options={ROLES.map((r) => ({ value: r, label: r }))}
                />
                <Button
                  variant="contained"
                  color="primary"
                  ariaLabel="Save role"
                  buttonText="Save role"
                  disabled={busy || roleDraft === user.role}
                  onClick={() => void saveRole()}
                />
              </Box>

              {/* Lifecycle actions */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  sx={DANGER_BTN}
                  ariaLabel="Suspend access"
                  buttonText="Suspend"
                  disabled={busy}
                  onClick={() => void setSuspended(true)}
                />
                <Button
                  variant="outlined"
                  sx={NEUTRAL_BTN}
                  ariaLabel="Reactivate access"
                  buttonText="Reactivate"
                  disabled={busy}
                  onClick={() => void setSuspended(false)}
                />
                {isDeleted ? (
                  <Button
                    variant="contained"
                    color="primary"
                    ariaLabel="Restore user"
                    buttonText="Restore"
                    disabled={busy}
                    onClick={() => void doRestore()}
                  />
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    ariaLabel="Delete user"
                    buttonText="Delete"
                    disabled={busy}
                    onClick={() => void doDelete()}
                  />
                )}
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="text"
                  ariaLabel="View audit history"
                  buttonText="Audit history"
                  onClick={() => navigate(`/audit-log?entityType=user&entityId=${user.glpUserId}`)}
                />
              </Box>
              </>}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}
